import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import type { Artwork, ArtworkRevision, ChildEdition, ChildProfile, StoryProgress } from '@/contracts/family';
import { getStory, STORIES } from '@/content/stories';
import { ArtworkService } from '@/services/artwork';
import { ProgressService } from '@/services/progress';
import { EditionService, BADGES, BadgeService } from '@/services/editions';
import { useSession } from '@/services/session';
import { buildKeepsakePdf, dataUrlToBlob, renderPrintableSketch, type ExportProgress, type PaperSize } from '@/services/exports';
import { Platform } from '@/platform';
import { LocalStore } from '@/platform/localStore';
import { Button } from '@/design-system/Button';
import { Card, Chip, EmptyState, SectionHeading } from '@/design-system/Surfaces';
import { SparkleGlyph } from '@/design-system/Sparkle';
import { Logo } from '@/design-system/Logo';
import { cn } from '@/design-system/cn';
import { avatarFor } from '@/features/family/FamilyApp';
import { CAPABILITIES } from '@/contracts/brand';
import { SyncPanel, useSyncing } from './SyncPanel';
import { SyncService } from '@/services/sync';

/* ------------------------------------------------------------------ *
 * Shell
 * ------------------------------------------------------------------ */

const NAV = [
  { to: '/app/parent', end: true, label: 'Overview' },
  { to: '/app/parent/keepsakes', label: 'Keepsakes' },
  { to: '/app/parent/settings', label: 'Settings' },
];

export function ParentShell() {
  const revoke = useSession((s) => s.revokeAdult);
  const navigate = useNavigate();

  return (
    <div className="min-h-[100dvh] bg-paper">
      <header className="border-b-2 border-line bg-card">
        <div className="u-shell flex flex-wrap items-center gap-m py-m">
          <Logo size="sm" />
          <Chip tone="neutral">Grown-ups</Chip>
          <div className="flex-1" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              // Leaving the parent area drops the elevation immediately.
              revoke();
              navigate('/app');
            }}
          >
            Back to child mode
          </Button>
        </div>
        <nav aria-label="Parent sections" className="u-shell flex gap-xs overflow-x-auto pb-s u-no-scrollbar">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                cn(
                  'whitespace-nowrap rounded-pebble px-m py-s font-display text-[15px] font-semibold',
                  isActive ? 'bg-primary-soft text-primary-deep' : 'text-ink-soft hover:bg-paper-deep',
                )
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="u-shell py-xl">
        <Outlet />
      </main>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Shared loading
 * ------------------------------------------------------------------ */

interface ChildSummary {
  child: ChildProfile;
  progress: StoryProgress[];
  artworks: Artwork[];
  finished: Array<{ artwork: Artwork; revision: ArtworkRevision; url: string | null }>;
  editions: ChildEdition[];
  badges: string[];
}

async function loadSummary(account: string, child: ChildProfile): Promise<ChildSummary> {
  const progress = await ProgressService.listForChild(account, child.id);
  const artworks = await ArtworkService.listForChild(account, child.id);
  const finished: ChildSummary['finished'] = [];
  for (const a of artworks) {
    if (!a.finishedRevisionId) continue;
    const rev = await ArtworkService.getRevision(account, a.finishedRevisionId);
    if (!rev) continue;
    finished.push({ artwork: a, revision: rev, url: await ArtworkService.previewUrl(account, rev) });
  }
  return {
    child,
    progress,
    artworks,
    finished,
    editions: await EditionService.listForChild(account, child.id),
    badges: await BadgeService.earnedIds(account, child.id),
  };
}

/**
 * Progress written as description, never as a score (spec §12).
 * Each sentence states only what was actually recorded.
 */
function describeProgress(s: ChildSummary): string[] {
  const out: string[] = [];
  const completed = s.progress.filter((p) => p.firstCompletedAt);
  for (const p of completed) {
    const story = getStory(p.storyId);
    if (!story) continue;
    const how =
      p.participationMethod === 'accommodation'
        ? 'went straight to making a picture'
        : p.participationMethod === 'helped'
          ? 'worked through the story activity with help'
          : 'worked through the story activity';
    out.push(`Read ${story.title} and ${how}.`);
  }
  const started = s.progress.filter((p) => !p.firstCompletedAt && p.currentPageId);
  for (const p of started) {
    const story = getStory(p.storyId);
    if (!story) continue;
    const idx = story.currentVersion.pages.findIndex((pg) => pg.id === p.currentPageId);
    out.push(`Part-way through ${story.title} — page ${Math.max(1, idx + 1)}.`);
  }
  if (s.finished.length) {
    const titles = [...new Set(s.finished.map((f) => f.artwork.title))];
    out.push(
      `Finished ${s.finished.length} picture${s.finished.length === 1 ? '' : 's'}: ${titles.join(', ')}.`,
    );
  }
  const withArt = s.editions.filter((e) => Object.keys(e.slots).length || e.coverRevisionId);
  if (withArt.length) out.push(`Put their own pictures into ${withArt.length} personal copy of a story.`);
  if (out.length === 0) out.push('Nothing recorded yet — nothing has been started on this device.');
  return out;
}

/* ------------------------------------------------------------------ *
 * P01 — Overview
 * ------------------------------------------------------------------ */

export function ParentDashboard() {
  const syncing = useSyncing();
  const account = useSession((s) => s.accountId);
  const children = useSession((s) => s.children);
  const [summaries, setSummaries] = useState<ChildSummary[]>([]);
  const [storage, setStorage] = useState<{ usage: number; quota: number } | null>(null);

  useEffect(() => {
    (async () => {
      setSummaries(await Promise.all(children.map((c) => loadSummary(account, c))));
      setStorage(await LocalStore.estimate());
    })();
  }, [account, children]);

  if (children.length === 0) {
    return (
      <EmptyState
        title="No children set up yet"
        body="Create a profile and their reading and pictures will appear here."
        action={<Button variant="magic" to="/app/setup">Set up a child</Button>}
      />
    );
  }

  return (
    <>
      <SectionHeading
        eyebrow="Overview"
        title="What has been happening"
        description="A description of what was actually done. There are no reading levels or ability scores here, because nothing this app measures would support one."
      />

      <div className="mt-xl grid gap-l lg:grid-cols-2">
        {summaries.map((s) => {
          const a = avatarFor(s.child.avatar);
          return (
            <Card key={s.child.id}>
              <div className="flex items-center gap-m">
                <span className={cn('grid h-12 w-12 place-items-center rounded-full text-[26px]', a.tint)}>
                  <span aria-hidden>{a.emoji}</span>
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-display text-[19px] font-semibold text-ink">{s.child.nickname}</h3>
                  <p className="text-[14px] text-ink-faint">
                    Age band {s.child.ageBand.replace('-', ' to ')} ·{' '}
                    {s.child.readingMode.replace(/-/g, ' ')}
                  </p>
                </div>
                <Button variant="outline" size="sm" to={`/app/parent/children/${s.child.id}`}>
                  Details
                </Button>
              </div>

              <ul className="mt-l space-y-s" data-testid="progress-lines">
                {describeProgress(s).map((line) => (
                  <li key={line} className="flex gap-s text-[16px] leading-relaxed text-ink-soft">
                    <SparkleGlyph className="mt-[5px] h-3.5 w-3.5 shrink-0 text-magic" />
                    {line}
                  </li>
                ))}
              </ul>

              {s.finished.length > 0 ? (
                <div className="mt-l flex flex-wrap gap-s">
                  {s.finished.slice(0, 4).map((f) =>
                    f.url ? (
                      <img
                        key={f.revision.id}
                        src={f.url}
                        alt={f.artwork.title}
                        className="h-16 w-16 rounded-pebble border-2 border-line bg-white object-contain"
                      />
                    ) : null,
                  )}
                </div>
              ) : null}
            </Card>
          );
        })}
      </div>

      <Card className="mt-l">
        <h3 className="font-display text-[18px] font-semibold text-ink">Where this is stored</h3>
        <p className="mt-s max-w-2xl text-[16px] leading-relaxed text-ink-soft">
          {syncing
            ? 'Everything above is on this device and on the sync service you signed in to, so it also appears on your other devices. Nothing is public and nothing is shared with anyone.'
            : 'Everything above is on this device only. You are not signed in, so nothing has been uploaded and nothing will appear on another device.'}
          {storage
            ? ` This app and site are currently using about ${(storage.usage / 1_048_576).toFixed(1)} MB of the ${(storage.quota / 1_048_576).toFixed(0)} MB the browser allows.`
            : ' The browser will not report how much space is in use.'}
        </p>
        <p className="mt-m text-[15px] text-ink-faint">
          Browser storage can be cleared by the browser itself. Until downloads are built,
          treat exports as the way to keep something permanently.
        </p>
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * P02 — Child detail
 * ------------------------------------------------------------------ */

export function ParentChildDetail() {
  const account = useSession((s) => s.accountId);
  const { childId } = useParams();
  const child = useSession((s) => s.requireChild(childId));
  const updateChild = useSession((s) => s.updateChild);
  const [summary, setSummary] = useState<ChildSummary | null>(null);

  useEffect(() => {
    if (child) void loadSummary(account, child).then(setSummary);
  }, [account, child]);

  if (!child) return <EmptyState title="That profile is not on this account" />;
  if (!summary) return null;

  const prompts = STORIES.filter((s) =>
    summary.progress.some((p) => p.storyId === s.id && p.firstCompletedAt),
  );

  return (
    <>
      <Button variant="ghost" size="sm" to="/app/parent">
        Back to overview
      </Button>
      <SectionHeading className="mt-l" eyebrow={child.nickname} title="Reading support and progress" />

      <div className="mt-xl grid gap-l lg:grid-cols-2">
        <Card>
          <h3 className="font-display text-[18px] font-semibold text-ink">Reading support</h3>
          <p className="mt-xs text-[15px] text-ink-soft">
            Change this any time. It never restarts a story or resets a picture.
          </p>
          <div className="mt-m grid gap-s">
            {([
              ['listen-together', 'Listen together', 'The story reads itself while you sit together.'],
              ['read-with-help', 'Read with help', 'They read; a tap hears a page again.'],
              ['read-myself', 'Read myself', 'Narration off, help still one tap away.'],
            ] as const).map(([id, label, desc]) => (
              <button
                key={id}
                onClick={() => void updateChild(child.id, { readingMode: id })}
                aria-pressed={child.readingMode === id}
                className={cn(
                  'rounded-pebble border-2 p-l text-left',
                  child.readingMode === id ? 'border-primary bg-primary-soft' : 'border-line bg-card',
                )}
              >
                <span className="block font-display text-[16px] font-semibold text-ink">{label}</span>
                <span className="mt-xs block text-[14px] text-ink-soft">{desc}</span>
              </button>
            ))}
          </div>
          <p className="mt-m text-[14px] text-ink-faint">
            Choosing a mode is your call, not a test result. None of these is the “advanced” one.
          </p>

          <div className="u-hairline mt-l pt-l">
            <div className="flex items-start gap-m">
              <input
                id="bedtime-mode"
                type="checkbox"
                checked={child.bedtime}
                onChange={(e) => void updateChild(child.id, { bedtime: e.target.checked })}
                className="mt-1 h-6 w-6 shrink-0 accent-magic"
              />
              <label htmlFor="bedtime-mode" className="cursor-pointer">
                <span className="block font-display text-[15px] font-semibold text-ink">
                  Bedtime mode
                </span>
                <span className="mt-xs block text-[14px] text-ink-soft">
                  Softer colours, optional effects off, and an offer to finish making a picture
                  tomorrow instead of starting one now. Nothing is scheduled and no reminder is
                  sent.
                </span>
              </label>
            </div>
          </div>
        </Card>

        <div className="space-y-l">
          <Card>
            <h3 className="font-display text-[18px] font-semibold text-ink">What they have done</h3>
            <ul className="mt-m space-y-s" data-testid="progress-lines">
              {describeProgress(summary).map((line) => (
                <li key={line} className="flex gap-s text-[16px] leading-relaxed text-ink-soft">
                  <SparkleGlyph className="mt-[5px] h-3.5 w-3.5 shrink-0 text-magic" />
                  {line}
                </li>
              ))}
            </ul>
            <p className="mt-m text-[14px] text-ink-faint">
              We do not record how long they looked at the screen, because a meaningful measure of
              attention is not something this app can take honestly.
            </p>
          </Card>

          {prompts.length > 0 ? (
            <Card className="bg-accent-soft/50">
              <h3 className="font-display text-[18px] font-semibold text-ink">Something to talk about</h3>
              {prompts.map((s) => (
                <p key={s.id} className="mt-m text-[16px] leading-relaxed text-ink-soft">
                  <span className="font-semibold text-ink">{s.title}: </span>
                  {s.conversationPrompt}
                </p>
              ))}
            </Card>
          ) : null}

          {summary.badges.length > 0 ? (
            <Card>
              <h3 className="font-display text-[18px] font-semibold text-ink">Badges earned</h3>
              <div className="mt-m flex flex-wrap gap-s">
                {BADGES.filter((b) => summary.badges.includes(b.id)).map((b) => (
                  <span
                    key={b.id}
                    className="inline-flex items-center gap-s rounded-full border-2 border-line bg-card px-l py-s"
                  >
                    <span aria-hidden className="text-[18px]">{b.emoji}</span>
                    <span className="font-display text-[14px] font-semibold text-ink">{b.name}</span>
                  </span>
                ))}
              </div>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * P04 — Keepsakes
 * ------------------------------------------------------------------ */

type Job =
  | { state: 'idle' }
  | { state: 'working'; progress: ExportProgress | null }
  | { state: 'ready'; blob: Blob; filename: string; previewUrl: string | null }
  | { state: 'failed'; message: string };

export function ParentKeepsakes() {
  const account = useSession((s) => s.accountId);
  const children = useSession((s) => s.children);
  const [childId, setChildId] = useState<string | null>(null);
  const [summary, setSummary] = useState<ChildSummary | null>(null);
  const [paper, setPaper] = useState<PaperSize>('a4');
  const [includeNickname, setIncludeNickname] = useState(false);
  const [job, setJob] = useState<Job>({ state: 'idle' });

  const child = useMemo(
    () => children.find((c) => c.id === (childId ?? children[0]?.id)) ?? null,
    [children, childId],
  );

  useEffect(() => {
    if (child) void loadSummary(account, child).then(setSummary);
  }, [account, child]);

  const reset = useCallback(() => setJob({ state: 'idle' }), []);

  const [deliverNote, setDeliverNote] = useState<string | null>(null);

  const deliver = async () => {
    if (job.state !== 'ready') return;
    setDeliverNote(null);
    // Cancelling the share sheet is not a failure and loses nothing (§10.4).
    const result = await Platform.share.export({
      blob: job.blob,
      filename: job.filename,
      title: job.filename,
    });
    if (result.status === 'cancelled') return;
    if (result.status === 'unsupported') {
      setDeliverNote(
        'This shared preview runs in a sandbox that will not let a page save files to your device, so nothing was downloaded. The file itself was built correctly — it saves normally in the real app.',
      );
    }
  };

  const runSketch = async (templateId: string, storyId: string) => {
    setJob({ state: 'working', progress: { step: 'Drawing the outline', done: 0, total: 1 } });
    try {
      const tpl = getStory(storyId)?.currentVersion.templates.find((t) => t.id === templateId);
      if (!tpl) throw new Error('missing-template');
      const dataUrl = await renderPrintableSketch(tpl);
      setJob({
        state: 'ready',
        blob: dataUrlToBlob(dataUrl),
        filename: `${tpl.title.replace(/\W+/g, '-').toLowerCase()}-to-colour.png`,
        previewUrl: dataUrl,
      });
    } catch {
      setJob({ state: 'failed', message: 'That sketch could not be prepared. Nothing was changed.' });
    }
  };

  const runPng = async (revision: ArtworkRevision, title: string, url: string | null) => {
    setJob({ state: 'working', progress: { step: 'Preparing the picture', done: 0, total: 1 } });
    try {
      const blob = await ArtworkService.previewUrl(account, revision).then(async (u) => {
        if (!u) throw new Error('missing-preview');
        return (await fetch(u)).blob();
      });
      setJob({
        state: 'ready',
        blob,
        filename: `${title.replace(/\W+/g, '-').toLowerCase()}.png`,
        previewUrl: url,
      });
    } catch {
      setJob({ state: 'failed', message: 'That picture could not be prepared. Nothing was changed.' });
    }
  };

  const runPdf = async (edition: ChildEdition) => {
    const story = getStory(edition.storyId);
    if (!story || !child) return;
    setJob({ state: 'working', progress: null });
    try {
      const blob = await buildKeepsakePdf(account, story, edition, {
        paper,
        includeNickname,
        nickname: child.nickname,
        onProgress: (p) => setJob({ state: 'working', progress: p }),
      });
      setJob({
        state: 'ready',
        blob,
        filename: `${story.slug}-personal-copy.pdf`,
        previewUrl: null,
      });
    } catch {
      setJob({
        state: 'failed',
        message: 'The book could not be built. Your child’s pictures are untouched — try again.',
      });
    }
  };

  if (children.length === 0) {
    return <EmptyState title="Nothing to export yet" body="Set up a child and make a picture first." />;
  }

  return (
    <>
      <SectionHeading
        eyebrow="Keepsakes"
        title="Print it, keep it, send it to a grandparent"
        description="Everything here is made from the finished picture, not from what is on screen. Nothing is uploaded — the file is handed to your device."
      />

      {children.length > 1 ? (
        <div className="mt-l flex flex-wrap gap-s">
          {children.map((c) => (
            <button
              key={c.id}
              onClick={() => {
                setChildId(c.id);
                reset();
              }}
              aria-pressed={child?.id === c.id}
              className={cn(
                'min-h-touch rounded-pebble border-2 px-l font-display text-[15px] font-semibold',
                child?.id === c.id ? 'border-magic bg-magic/10 text-magic-deep' : 'border-line bg-card text-ink-soft',
              )}
            >
              {c.nickname}
            </button>
          ))}
        </div>
      ) : null}

      {job.state !== 'idle' ? (
        <Card className="mt-l" aria-live="polite">
          {job.state === 'working' ? (
            <>
              <h3 className="font-display text-[18px] font-semibold text-ink">Preparing…</h3>
              <p className="mt-xs text-[15px] text-ink-soft">
                {job.progress ? `${job.progress.step} (${job.progress.done} of ${job.progress.total})` : 'Starting'}
              </p>
              <div className="mt-m h-2 w-full overflow-hidden rounded-full bg-paper-deep">
                <div
                  className="h-full rounded-full bg-magic transition-[width]"
                  style={{
                    width: job.progress ? `${(job.progress.done / job.progress.total) * 100}%` : '8%',
                  }}
                />
              </div>
            </>
          ) : null}

          {job.state === 'ready' ? (
            <>
              <h3 className="font-display text-[18px] font-semibold text-ink">Ready</h3>
              <p className="mt-xs text-[15px] text-ink-soft">{job.filename}</p>
              {job.previewUrl ? (
                <img
                  src={job.previewUrl}
                  alt="Preview of the export"
                  className="mt-m max-h-64 rounded-pebble border-2 border-line bg-white"
                />
              ) : null}
              <div className="mt-l flex flex-wrap gap-m">
                <Button variant="magic" size="md" onClick={deliver}>
                  Save or share
                </Button>
                <Button variant="ghost" size="md" onClick={reset}>
                  Done
                </Button>
              </div>
              {!CAPABILITIES.fileSave ? (
                <p className="mt-m text-[14px] text-ink-faint">
                  Heads up: this shared preview cannot save files to your device.
                </p>
              ) : null}
              {deliverNote ? (
                <p role="status" className="mt-m text-[15px] text-secondary">
                  {deliverNote}
                </p>
              ) : null}
            </>
          ) : null}

          {job.state === 'failed' ? (
            <>
              <h3 className="font-display text-[18px] font-semibold text-secondary">
                That did not work
              </h3>
              <p className="mt-xs text-[15px] text-ink-soft">{job.message}</p>
              <Button variant="outline" size="md" className="mt-l" onClick={reset}>
                Try something else
              </Button>
            </>
          ) : null}
        </Card>
      ) : null}

      {/* ---- Personal editions -> PDF ---- */}
      <section className="mt-2xl">
        <h2 className="font-display text-[20px] font-semibold text-ink">Their own book</h2>
        <p className="mt-xs text-[15px] text-ink-soft">
          A PDF of their personal copy, with their pictures on the pages they chose.
        </p>

        <div className="mt-m flex flex-wrap items-center gap-l">
          <fieldset className="flex items-center gap-s">
            <legend className="u-sr">Paper size</legend>
            {(['a4', 'letter'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPaper(p)}
                aria-pressed={paper === p}
                className={cn(
                  'min-h-touch rounded-pebble border-2 px-l font-display text-[14px] font-semibold uppercase',
                  paper === p ? 'border-primary bg-primary-soft text-primary-deep' : 'border-line bg-card text-ink-soft',
                )}
              >
                {p}
              </button>
            ))}
          </fieldset>

          <div className="flex items-center gap-s">
            <input
              id="include-nickname"
              type="checkbox"
              checked={includeNickname}
              onChange={(e) => setIncludeNickname(e.target.checked)}
              className="h-6 w-6 accent-magic"
            />
            <label htmlFor="include-nickname" className="cursor-pointer text-[15px] text-ink-soft">
              Print their nickname on the cover
            </label>
          </div>
        </div>
        <p className="mt-xs text-[13px] text-ink-faint">
          Off by default — an export carries no identifying detail unless you add it.
        </p>

        {!CAPABILITIES.pdfExport ? (
          <Card className="mt-l bg-sunny/15">
            <h3 className="font-display text-[17px] font-semibold text-ink">
              Not in this shared preview
            </h3>
            <p className="mt-s text-[16px] leading-relaxed text-ink-soft">
              Building the book needs a PDF engine that would roughly triple the
              size of this shareable preview, so it is left out here. It works in
              the real app — printable sketches and picture downloads below still
              work in both.
            </p>
          </Card>
        ) : !summary || summary.editions.length === 0 ? (
          <div className="mt-l">
            <EmptyState
              title="No personal copy yet"
              body="Once a picture goes into their own copy of a story, you can print the whole book."
            />
          </div>
        ) : (
          <div className="mt-l grid gap-l sm:grid-cols-2">
            {summary.editions.map((e) => {
              const story = getStory(e.storyId);
              const count = Object.keys(e.slots).length;
              return (
                <Card key={e.id}>
                  <h3 className="font-display text-[17px] font-semibold text-ink">{story?.title}</h3>
                  <Chip tone={count ? 'magic' : 'neutral'} className="mt-s">
                    {count === 0 ? 'No pictures added yet' : `${count} of their pictures`}
                  </Chip>
                  <Button
                    variant="magic"
                    size="md"
                    className="mt-m"
                    disabled={job.state === 'working'}
                    onClick={() => runPdf(e)}
                  >
                    Make the PDF
                  </Button>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {/* ---- Finished pictures -> PNG ---- */}
      <section className="mt-2xl">
        <h2 className="font-display text-[20px] font-semibold text-ink">Finished pictures</h2>
        {!summary || summary.finished.length === 0 ? (
          <p className="mt-s text-[15px] text-ink-soft">Nothing finished yet.</p>
        ) : (
          <div className="mt-l grid gap-l sm:grid-cols-3">
            {summary.finished.map((f) => (
              <Card key={f.revision.id}>
                <div className="aspect-square w-full overflow-hidden rounded-pebble border-2 border-line bg-white">
                  {f.url ? (
                    <img src={f.url} alt={f.artwork.title} className="h-full w-full object-contain" />
                  ) : null}
                </div>
                <h3 className="mt-m font-display text-[16px] font-semibold text-ink">{f.artwork.title}</h3>
                <div className="mt-m flex flex-wrap gap-s">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={job.state === 'working'}
                    onClick={() => runPng(f.revision, f.artwork.title, f.url)}
                  >
                    Save picture
                  </Button>
                  {f.artwork.templateId && f.artwork.storyId ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={job.state === 'working'}
                      onClick={() => runSketch(f.artwork.templateId!, f.artwork.storyId!)}
                    >
                      Blank to colour
                    </Button>
                  ) : null}
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * P07 — Settings (only what is honestly implementable today)
 * ------------------------------------------------------------------ */

export function ParentSettings() {
  const account = useSession((s) => s.accountId);
  const syncing = useSyncing();
  const children = useSession((s) => s.children);
  const load = useSession((s) => s.load);
  const [confirming, setConfirming] = useState(false);
  const [done, setDone] = useState(false);

  const wipe = async () => {
    await LocalStore.clearAccount(account);
    try {
      localStorage.removeItem('examine.accountId');
    } catch {
      /* storage blocked */
    }
    await load();
    setDone(true);
  };

  return (
    <>
      <SectionHeading
        eyebrow="Settings"
        title="Privacy and data"
        description="Only the controls that actually do something are shown here."
      />

      <div className="mt-xl grid gap-l lg:grid-cols-2">
        <SyncPanel />
        <Card>
          <h3 className="font-display text-[18px] font-semibold text-ink">What is stored</h3>
          <ul className="mt-m space-y-s text-[16px] text-ink-soft">
            {[
              `${children.length} child profile${children.length === 1 ? '' : 's'} — nickname, preset avatar, age band, reading mode`,
              'Which page of a story was reached, and whether an activity was completed',
              'Finished pictures, drafts and personal editions',
            ].map((x) => (
              <li key={x} className="flex gap-s">
                <SparkleGlyph className="mt-[5px] h-3.5 w-3.5 shrink-0 text-ink-faint" />
                {x}
              </li>
            ))}
          </ul>
          <p className="mt-m text-[15px] text-ink-faint">
            {syncing
              ? 'On this device, and on the sync service you signed in to. Nothing is public and nothing is shared.'
              : 'All on this device. You are not signed in, so none of it has left this device.'}
          </p>
        </Card>

        <Card className="border-secondary/40">
          <h3 className="font-display text-[18px] font-semibold text-ink">Delete everything</h3>
          <p className="mt-s text-[16px] leading-relaxed text-ink-soft">
            Removes every profile, every picture, every personal copy and all progress this app has
            written on this device. It cannot be undone, and exports you have already saved are not
            affected.
            {syncing
              ? ' You are signed in, so copies on the sync service and on your other devices are not removed by this.'
              : ' You are not signed in, so this is the only copy and the deletion is complete.'}
          </p>

          {done ? (
            <p role="status" className="mt-l font-display text-[16px] font-semibold text-leaf">
              Everything has been deleted from this device.
            </p>
          ) : confirming ? (
            <div className="mt-l flex flex-wrap gap-m">
              <Button variant="danger" size="md" onClick={wipe}>
                Yes, delete it all
              </Button>
              <Button variant="ghost" size="md" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button variant="outline" size="md" className="mt-l" onClick={() => setConfirming(true)}>
              Delete all data on this device
            </Button>
          )}
        </Card>
      </div>

      <Card className="mt-l bg-sunny/15">
        <h3 className="font-display text-[18px] font-semibold text-ink">Not built yet</h3>
        <ul className="mt-m space-y-s text-[16px] text-ink-soft">
          {[
            'Public sharing approvals — the gallery is not open, so there is nothing to approve',
            'Membership and billing — no products are configured',
            'Downloads and sync — the app is device-only for now',
            'Verified parental consent — needs a children’s-privacy review to choose the method',
          ].map((x) => (
            <li key={x} className="flex gap-s">
              <span aria-hidden className="mt-[8px] h-1.5 w-1.5 shrink-0 rounded-full bg-ink-faint" />
              {x}
            </li>
          ))}
        </ul>
        <p className="mt-m text-[15px] text-ink-faint">
          These are absent rather than shown greyed out, so nothing here looks like a control that
          simply is not working. <Link to="/privacy" className="text-primary underline">Read the privacy page</Link>.
        </p>
      </Card>
    </>
  );
}
